import { useMemo } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { Stack } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text } from "@src/components";
import { Checkbox } from "@src/components/Checkbox";
import { getCameraKeyString } from "@src/utils/key-parser";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { PressableScale } from "pressto";
import { useManageAlarmResponse } from "./useManageAlarmResponse";

export default function ManageAlarmResponseScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const { headerLeft } = useHeaderBackButton();

  const {
    siteCameras,
    isLoading,
    effectiveResponses,
    toggleResponse,
    allSirensOn,
    allStrobesOn,
    toggleAllSirens,
    toggleAllStrobes,
    hasChanges,
    isSaving,
    save,
  } = useManageAlarmResponse();

  const headerTitle = useMemo(
    () => (
      <View flexDirection="row" justifyContent="space-between" alignItems="center" flex={1}>
        <View flex={1}>
          <Text textStyle="md/semibold" color="text" textAlign="center" style={localStyles.headerTitle}>
            Alarm response
          </Text>
        </View>
        <PressableScale
          onPress={save}
          style={[
            localStyles.saveButton,
            {
              opacity: !isSaving && hasChanges ? 1 : 0.5,
            },
          ]}
          enabled={!isSaving && hasChanges}
        >
          {isSaving ?
            <ActivityIndicator size="small" />
          : <Text textStyle="md/medium" color="white">
              Save
            </Text>
          }
        </PressableScale>
      </View>
    ),
    [isSaving, hasChanges, save],
  );

  return (
    <>
      <Stack.Screen options={{ title: "Alarm response", headerLeft, headerTitle: () => headerTitle }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        {isLoading && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        {!isLoading && (
          <View>
            {/* Monitoring upsell UI is intentionally hidden for now.
                Keep `AlarmResponseShared` components for future re-enable. */}
            {/* Column headers */}
            <View style={localStyles.headerRow} px="5xl" gap="xxs">
              <View flex={1} />
              <View style={localStyles.columnHeader}>
                <View style={localStyles.textHeader}>
                  <Text textStyle="xs/medium" color="textMuted">
                    Sirens
                  </Text>
                </View>
                <Checkbox checked={allSirensOn} onToggle={toggleAllSirens} />
              </View>
              <View style={localStyles.columnHeader}>
                <View style={localStyles.textHeader}>
                  <Text textStyle="xs/medium" color="textMuted">
                    Strobes
                  </Text>
                </View>
                <Checkbox checked={allStrobesOn} onToggle={toggleAllStrobes} />
              </View>
            </View>

            {/* Camera rows */}
            {siteCameras.map((camera) => {
              const key = getCameraKeyString(camera);
              const state = effectiveResponses.get(key) ?? { sirens: "off", strobes: "off" };

              return (
                <View key={key} style={localStyles.row} px="5xl" gap="xxs">
                  <View flex={1}>
                    <Text textStyle="xxs/regular" color="text" numberOfLines={3}>
                      {key}
                    </Text>
                  </View>
                  <View style={localStyles.checkboxCell}>
                    <Checkbox checked={state.sirens === "on"} onToggle={() => toggleResponse(key, "sirens")} />
                  </View>
                  <View style={localStyles.checkboxCell}>
                    <Checkbox checked={state.strobes === "on"} onToggle={() => toggleResponse(key, "strobes")} />
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>
    </>
  );
}

const localStyles = StyleSheet.create((theme) => ({
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    borderBottomWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  columnHeader: {
    width: 72,
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  textHeader: {
    width: "100%",
    alignItems: "center",
    backgroundColor: theme.colors.borderCard,
    paddingVertical: theme.spacing.md,
    borderTopLeftRadius: theme.spacing.xs,
    borderTopRightRadius: theme.spacing.xs,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["7xl"],
  },
  checkboxCell: {
    width: 72,
    alignItems: "center",
  },
  saveButton: {
    paddingHorizontal: theme.spacing["5xl"],
    backgroundColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing["6xl"],
    marginRight: theme.spacing["5xl"],
    borderCurve: "continuous",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: {
    marginLeft: theme.spacing["5xl"],
  },
}));
